/**
 * <ry-theme-toggle>
 *
 * Simple theme switcher button.
 * Cycles through available themes or toggles light/dark.
 *
 * Usage:
 * <ry-theme-toggle></ry-theme-toggle>
 * <ry-theme-toggle themes="light,dark,ocean"></ry-theme-toggle>
 *
 * JS uses data-ry-target for queries, CSS uses classes for styling.
 */

import { RyElement } from '../core/ry-element.js';

interface ThemeChangeDetail {
  theme: string;
}

export class RyThemeToggle extends RyElement {
  #themes: string[] = ['light', 'dark'];
  #currentIndex = 0;

  setup(): void {
    // Parse themes attribute
    const themesAttr = this.getAttribute('themes');
    if (themesAttr) {
      this.#themes = themesAttr.split(',').map(t => t.trim());
    }

    // Get current theme
    const currentTheme = document.documentElement.dataset.ryTheme ?? 'light';
    this.#currentIndex = this.#themes.indexOf(currentTheme);
    if (this.#currentIndex === -1) this.#currentIndex = 0;

    // Add button if empty
    if (!this.innerHTML.trim()) {
      this.innerHTML = `<button data-ry-target="button" class="ry-btn ry-btn--ghost">${this.#getIcon()}</button>`;
    }

    // Bind click
    this.on(this, 'click', () => this.toggle());
  }

  #getIcon(): string {
    const theme = this.#themes[this.#currentIndex];
    switch (theme) {
      case 'dark':
        return '&#9790;'; // Moon
      case 'ocean':
        return '&#127754;'; // Wave
      default:
        return '&#9728;'; // Sun
    }
  }

  toggle(): void {
    this.#currentIndex = (this.#currentIndex + 1) % this.#themes.length;
    const newTheme = this.#themes[this.#currentIndex]!;

    document.documentElement.dataset.ryTheme = newTheme;

    // Update icon
    const btn = this.$('[data-ry-target="button"]');
    if (btn) {
      btn.innerHTML = this.#getIcon();
    }

    this.emit<ThemeChangeDetail>('theme-change', { theme: newTheme });
  }

  get theme(): string {
    return this.#themes[this.#currentIndex] ?? 'light';
  }

  set theme(value: string) {
    const index = this.#themes.indexOf(value);
    if (index !== -1) {
      this.#currentIndex = index;
      document.documentElement.dataset.ryTheme = value;

      const btn = this.$('[data-ry-target="button"]');
      if (btn) {
        btn.innerHTML = this.#getIcon();
      }
    }
  }
}

customElements.define('ry-theme-toggle', RyThemeToggle);
